import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  VISIT_TYPES,
  checklistDefinitionSchema,
  type ChecklistDefinition,
  type ChecklistVisitType,
} from '@shared/checklists/stack.ts';
import type { VisitChecklist } from '@shared/types/index.ts';
import { checklistStackApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { usePlanFeature } from '../plan-gate.tsx';
import { Alert, Button, Select } from '../ui.tsx';
import { useChecklistEditScope } from '../checklist-edit-scope.ts';
import { ChecklistDefinitionEditor, newChecklistDefinition } from '../checklist-definition-editor.tsx';
import { definitionFromVisit } from '@shared/checklists/visit-definition.ts';

export { definitionFromVisit };
export function VisitChecklistEditor({
  appointmentId,
  checklist,
}: {
  appointmentId: string;
  checklist: VisitChecklist;
}) {
  const { t } = useTranslation();
  const scopeGuard = useChecklistEditScope('schedule:write');
  const photoAllowed = usePlanFeature('checklist_reports');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<ChecklistDefinition | null>(null);
  const [scope, setScope] = useState<'this_visit' | 'this_and_upcoming'>('this_visit');
  const [visitType, setVisitType] = useState<ChecklistVisitType | null>(checklist.visitType ?? null);
  const [error, setError] = useState<string | null>(null);
  const [changed, setChanged] = useState<number | null>(null);
  const request = useRef(crypto.randomUUID());
  const originalRevision = useRef(checklist.revision ?? 1);
  const boundary = useRef({
    expectedStartsAt: checklist.scopeStartsAt ?? '',
    expectedOccurrenceDate: checklist.occurrenceDate ?? '',
  });
  const save = useMutation({
    mutationFn: async () => {
      if (!scopeGuard.allowed) throw new Error('The checklist edit scope changed.');
      const editScope = scopeGuard.stamp;
      const result = await checklistStackApi.editVisit(appointmentId, {
        requestId: request.current,
        revision: originalRevision.current,
        ...boundary.current,
        scope,
        visitType,
        definition: value!,
      });
      return { ...result, editScope };
    },
    onSuccess: async (result) => {
      if (!scopeGuard.isCurrent(result.editScope)) return;
      setEditing(false);
      setError(null);
      setChanged(result.changed);
      await queryClient.invalidateQueries({ queryKey: ['visit-checklist'] });
      await queryClient.invalidateQueries({ queryKey: ['schedule'] });
    },
    onError: (caught) =>
      scopeGuard.isCurrent() &&
      setError(
        t(
          caught instanceof ApiRequestError && caught.status === 409
            ? 'checklistStack.stale'
            : 'checklistStack.saveError',
        ),
      ),
  });
  const change = (next: ChecklistDefinition) => {
    setValue(next);
    request.current = crypto.randomUUID();
  };
  if (!scopeGuard.sameScope) return <Alert tone="error">{t('checklistStack.stale')}</Alert>;
  return (
    <div className="my-3 space-y-3">
      {changed !== null && <Alert tone="success">{t('checklistStack.changed', { count: changed })}</Alert>}
      {!editing ? (
        <Button
          disabled={!scopeGuard.allowed || checklist.definitionEditable === false || checklist.readOnly}
          onClick={() => {
            setEditing(true);
            setValue(
              checklist.items.length
                ? definitionFromVisit(checklist, t('checklists.visitHeading'))
                : newChecklistDefinition(),
            );
            originalRevision.current = checklist.revision ?? 1;
            boundary.current = {
              expectedStartsAt: checklist.scopeStartsAt ?? '',
              expectedOccurrenceDate: checklist.occurrenceDate ?? '',
            };
            request.current = crypto.randomUUID();
            setError(null);
          }}
        >
          {t('checklistStack.editVisit')}
        </Button>
      ) : (
        <form
          className="space-y-3 rounded-xl border border-[color:var(--color-line)] p-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!scopeGuard.allowed) return;
            if (!checklistDefinitionSchema.safeParse(value).success) {
              setError(t('checklistStack.invalid'));
              return;
            }
            save.mutate();
          }}
        >
          {error && <Alert tone="error">{error}</Alert>}
          <Select
            label={t('checklistStack.scope')}
            disabled={save.isPending || !scopeGuard.allowed}
            value={scope}
            options={[
              { value: 'this_visit', label: t('checklistStack.thisVisit') },
              { value: 'this_and_upcoming', label: t('checklistStack.upcoming') },
            ]}
            onChange={(event) => {
              setScope(event.target.value as typeof scope);
              request.current = crypto.randomUUID();
            }}
          />
          <Select
            label={t('checklistStack.visitType')}
            disabled={save.isPending || !scopeGuard.allowed}
            value={visitType ?? ''}
            options={[
              { value: '', label: t('checklistStack.legacyType') },
              ...VISIT_TYPES.map((type) => ({ value: type, label: t(`checklistStack.types.${type}`) })),
            ]}
            onChange={(event) => {
              setVisitType((event.target.value || null) as ChecklistVisitType | null);
              request.current = crypto.randomUUID();
            }}
          />
          <Alert tone="info">{t('checklistStack.scopeHint')}</Alert>
          {value && (
            <ChecklistDefinitionEditor
              value={value}
              onChange={change}
              photoAllowed={photoAllowed}
              disabled={save.isPending || !scopeGuard.allowed}
            />
          )}
          <div className="flex gap-2">
            <Button type="submit" variant="primary" busy={save.isPending} disabled={!scopeGuard.allowed}>
              {t('common.save')}
            </Button>
            <Button disabled={save.isPending || !scopeGuard.allowed} onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
